import { DialogContent, CircularProgress, Typography } from '@material-ui/core';
import PropTypes from 'prop-types';
import React, { useMemo, useState, useCallback } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { makeStyles } from '@material-ui/core/styles';
import {
  usePutBillFormState,
  usePutBillHandler
} from '~/modules/billing-invoicing/bill/hooks';
import { isNullOrUndefined } from '~/modules/common/util';
import {
  useIsBillDataReflectedOnAvailableToBill,
  useNextReferenceNumberText
} from '~/modules/billing-invoicing/common/hooks';
import { useMeContext } from '~/modules/me';
import { billingTransactionType } from '~/modules/billing-invoicing/common/enums';
import { ProgressContent } from '~/modules/billing-invoicing/common/components';
import BillInfo from '../../bill/components/BillDetails/BillInfo';
import AddLineItemsDialogContent from './AddLineItemsDialogContent';
import AddLineItemsDialogFooter from './AddLineItemsDialogFooter';
import BillingItemGroup from './BillingItemGroup';
import { useLineItemsDialogContext } from './AddLineItemsDialogContext';
import {
  useFilterBasedOnSearchCriteria,
  useCreateInvoiceItemsFromBillItems,
  useAddLineItemsHooks,
  useCreateInvoiceFromBillItemsBatch,
  useCreateBillFromBillingItemsBatchResults,
  useBillingDefaults
} from './hooks';
import getFacets from './facets';
import Toolbar from './Toolbar';
import { useStyles } from './useStyles';

const useProgressStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    padding: theme.spacing(3),
    boxSizing: 'border-box',
    justifyContent: 'center',
    minHeight: '415px'
  },
  infoMessage: {
    marginTop: theme.spacing(1),
    fontWeight: theme.typography.fontWeightMedium
  }
}));

export const AddLineItemsTab = ({
  billId,
  onClose,
  client,
  createBill,
  billCurrency,
  setLineItemAdding,
  projectUri,
  summarizeColumnOptions,
  setSummarizeColumOptions,
  billingBatchState,
  setBillingBatchState,
  availableToBillProject,
  projectName,
  refetchAvailableToBillRows,
  refetchAvailableToBillTotal,
  refetchFilteredTotals,
  startDate,
  endDate,
  billDetails
}) => {
  const me = useMeContext();
  const intl = useIntl();
  const {
    isPsaPrpAddProjectColumnOnAvailableToBillEnabled,
    isPsaPrpPreventZeroAmountEnabled,
    isPsaPrpBillUiEnhancementsEnabled
  } = me.featureFlags;
  const classes = useStyles();
  const progressClasses = useProgressStyles();
  const history = useHistory();
  const { params } = useRouteMatch();

  const { nextReferenceNumberText } = useNextReferenceNumberText(
    billingTransactionType.BILL
  );

  const {
    clientDefaultData,
    invoiceDefaultTemplate,
    billingDefaults
  } = useBillingDefaults({
    availableToBillProject,
    client,
    projectUri,
    startDate,
    endDate
  });
  const { defaultInvoicePaymentTerm } = clientDefaultData || {};
  const { putBill } = usePutBillHandler();
  const [selectedBillingItems, setSelectedBillingItems] = useState({});
  const [saving, setSaving] = useState(false);
  const [createAndView, setCreateAndView] = useState(false);
  const [
    isBillDataReflectedInAvailableToBillData,
    setIsBillDataReflectedInAvailableToBillData
  ] = useState(false);

  const shouldShowLoading = isPsaPrpBillUiEnhancementsEnabled
    ? isBillDataReflectedInAvailableToBillData
    : createAndView;

  const {
    values,
    setFieldValue,
    errors,
    validateForm,
    isSubmitting,
    setFieldError
  } = usePutBillFormState({
    projectUri,
    projectName,
    me,
    client,
    clientCurrency: billingDefaults?.invoiceCurrency,
    clientPaymentTerm:
      billingDefaults?.defaultInvoicePaymentTerm ?? defaultInvoicePaymentTerm,
    clientInvoiceTemplate: billingDefaults?.invoiceTemplate,
    clientTaxProfile: billingDefaults?.taxProfile,
    poNumber: !isNullOrUndefined(billingDefaults?.poNumber)
      ? billingDefaults?.poNumber
      : '',
    invoiceDefaultTemplate,
    putBill,
    setSaving,
    period: { startDate, endDate },
    billingAddress: billingDefaults?.billingContactInfo,
    onClose,
    summarizeColumnOptions,
    nextReferenceNumberText,
    billingSettings: {
      billLineItemsBy: billingDefaults?.billLineItemsBy,
      description: billingDefaults?.description,
      internalNotes: billingDefaults?.internalNotes,
      showComments: billingDefaults?.showComments
    }
  });
  const setColumns = useCallback(
    col => {
      setFieldValue('summarizeColumnOptions', col);
    },
    [setFieldValue]
  );

  const { searchCriteria } = useLineItemsDialogContext();
  const { billingItemSearch } = useFilterBasedOnSearchCriteria({
    searchCriteria
  });
  const searchFacets = useMemo(
    () => getFacets({ columns: values.summarizeColumnOptions, client }),
    [client, values.summarizeColumnOptions]
  );

  const {
    createInvoiceItemsFromBillingItemsBatch
  } = useCreateInvoiceItemsFromBillItems({ setBillingBatchState });
  const { fetchCalculationStatus } = useIsBillDataReflectedOnAvailableToBill();
  const { fetchBatchResults } = useCreateBillFromBillingItemsBatchResults();

  const addModeResourceKey = createBill
    ? 'addLineItemDialog.createBill'
    : 'addLineItemDialog.addLineItems';

  const {
    createInvoiceFromBillItemsBatch
  } = useCreateInvoiceFromBillItemsBatch({
    setBillingBatchState,
    isPsaPrpAddProjectColumnOnAvailableToBillEnabled,
    isPsaPrpPreventZeroAmountEnabled
  });

  const {
    onAddLineItems,
    onBatchComplete,
    onBatchComplete2,
    onBatchCompleteViewBill
  } = useAddLineItemsHooks({
    createBill,
    validateForm,
    setFieldError,
    setSaving,
    createAndView,
    setCreateAndView,
    billId,
    setSummarizeColumOptions,
    createInvoiceItemsFromBillingItemsBatch,
    createInvoiceFromBillItemsBatch,
    fetchCalculationStatus,
    fetchBatchResults,
    columns: values.summarizeColumnOptions,
    values,
    billingItemSearch,
    selectedBillingItems,
    setLineItemAdding,
    onClose,
    refetchAvailableToBillRows,
    refetchAvailableToBillTotal,
    refetchFilteredTotals,
    history,
    params,
    intl,
    putBill,
    billDetails,
    setBillingBatchState,
    setIsBillDataReflectedInAvailableToBillData,
    isPsaPrpBillUiEnhancementsEnabled
  });

  return (
    <>
      <DialogContent className={classes.dialogContent}>
        {isPsaPrpBillUiEnhancementsEnabled &&
        billingBatchState?.batchId &&
        billingBatchState?.batchInProgress ? (
          <ProgressContent
            classes={progressClasses}
            labelId={
              createBill || createAndView
                ? 'availableToBillList.creatingBill'
                : 'billingTransactionList.addingLineItems'
            }
            batchId={billingBatchState?.batchId}
            onBatchComplete={onBatchComplete2}
            queryType="billing_batch_status"
          />
        ) : (
          <>
            {shouldShowLoading ? (
              <div
                className={
                  isPsaPrpBillUiEnhancementsEnabled
                    ? classes.refreshingBillingDataContainer
                    : classes.loadingContainer
                }
              >
                <CircularProgress
                  size={24}
                  className={
                    isPsaPrpBillUiEnhancementsEnabled
                      ? undefined
                      : classes.progress
                  }
                />
                {isPsaPrpBillUiEnhancementsEnabled && (
                  <Typography variant="caption">
                    <FormattedMessage id="availableToBillList.refreshingBillingData" />
                  </Typography>
                )}
              </div>
            ) : (
              <>
                {Boolean(createBill) && (
                  <BillInfo
                    hasClient
                    isCollapsible
                    containerClass={classes.container}
                    fieldContainerClass={classes.fieldContainer}
                    currencyEditable
                    setFieldValue={setFieldValue}
                    errors={errors}
                    isSubmitting={isSubmitting}
                    values={values}
                  />
                )}
                <Toolbar
                  searchFacets={searchFacets}
                  columns={values.summarizeColumnOptions}
                  createBill={createBill}
                />
                <div className={classes.groupBar}>
                  <BillingItemGroup
                    columns={values.summarizeColumnOptions}
                    setColumns={setColumns}
                  />
                </div>
                <div className={classes.resultContainer}>
                  <AddLineItemsDialogContent
                    classes={classes}
                    billingItemSearch={billingItemSearch}
                    client={client}
                    columns={values.summarizeColumnOptions}
                    setSelectedBillingItems={setSelectedBillingItems}
                    billCurrency={
                      !createBill ? billCurrency : values.billCurrency
                    }
                    projectUri={projectUri}
                  />
                </div>
              </>
            )}
          </>
        )}
      </DialogContent>
      <AddLineItemsDialogFooter
        onAddLineItems={onAddLineItems}
        onBatchCompleteViewBill={onBatchCompleteViewBill}
        onClose={onClose}
        disableAdd={Boolean(
          saving ||
            createAndView ||
            !selectedBillingItems.any ||
            (isPsaPrpBillUiEnhancementsEnabled &&
              billingBatchState?.batchInProgress)
        )}
        addButtonLabel={addModeResourceKey}
        saveInProgress={saving}
        createAndViewInProgress={createAndView}
        billingBatchState={billingBatchState}
        setBillingBatchState={setBillingBatchState}
        onBatchComplete={onBatchComplete}
        createBill={createBill}
      />
    </>
  );
};

AddLineItemsTab.propTypes = {
  billId: PropTypes.string,
  setLineItemAdding: PropTypes.func,
  billCurrency: PropTypes.object,
  onClose: PropTypes.func,
  client: PropTypes.object,
  createBill: PropTypes.bool,
  availableToBillProject: PropTypes.object,
  projectUri: PropTypes.string,
  summarizeColumnOptions: PropTypes.array,
  setSummarizeColumOptions: PropTypes.func,
  billingBatchState: PropTypes.object,
  setBillingBatchState: PropTypes.func,
  projectName: PropTypes.string,
  refetchAvailableToBillRows: PropTypes.func,
  refetchAvailableToBillTotal: PropTypes.func,
  refetchFilteredTotals: PropTypes.func,
  startDate: PropTypes.object,
  endDate: PropTypes.object,
  billDetails: PropTypes.object.isRequired
};

export default AddLineItemsTab;
